import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import MenuFilter from './components/MenuFilter';
import MealPlan from './components/MealPlan';
import MealPlanPreview from './components/MealPlanPreview';
import MealPlanSuggestion from './components/MealPlanSuggestion';
import SharedMealPlan from './components/SharedMealPlan';
import MyMenusPreview from './components/MyMenusPreview';
import AffiliateRegistrationPreview from './components/AffiliateRegistrationPreview';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import FAQ from './components/FAQ';
import LandingPage from './components/LandingPage';
import Footer from './components/Footer';
import Navbar from './components/Navbar';
import AdminMenuUpload from './components/AdminMenuUpload';
import AffiliateDashboard from './components/AffiliateDashboard';
import AffiliateRegistration from './components/AffiliateRegistration';
import { GoogleOAuthProvider } from '@react-oauth/google';
import AdminDashboard from './components/AdminDashboard';
import FitnessAdminDashboard from './components/FitnessAdminDashboard';
import DevLogin from './components/DevLogin';
import { pageview, identifyUser } from './utils/analytics';
import { Toaster } from 'react-hot-toast';
import UserDashboard from './components/UserDashboard';
import FitnessHome from './components/fitness/FitnessHome';
import ProgramPage from './components/fitness/ProgramPage';
import WorkoutPage from './components/fitness/WorkoutPage';
import WorkoutsListPage from './components/fitness/WorkoutsListPage';
import ExercisesListPage from './components/fitness/ExercisesListPage';
import ExercisePage from './components/fitness/ExercisePage';
import ActiveWorkout from './components/fitness/ActiveWorkout';
import SessionDetailPage from './components/fitness/SessionDetailPage';
import SharedNutritionPlan from './components/SharedNutritionPlan';
import { NutritionPlannerWizard } from './components/NutritionPlanner';
import Pricing from './components/Pricing';
import UserProfile from './components/UserProfile';
import MenuPreferencesOnboarding from './components/MenuPreferencesOnboarding';
import SubscriptionSuccess from './components/SubscriptionSuccess';
import CoachInvite from './components/CoachInvite';
import AccountDeletion from './components/AccountDeletion';
import ShoppingCart from './components/ShoppingCart';
import CreatorPage from './components/CreatorPage';
import CreatorsPage from './components/CreatorsPage';
import CoachesPage from './components/CoachesPage';
import CoachProfilePage from './components/CoachProfilePage';
import MyWorkoutsPage from './components/MyWorkoutsPage';
import MyWorkoutsPreview from './components/MyWorkoutsPreview';
import WorkoutCreator from './components/fitness/WorkoutCreator';
import ProgramCreator from './components/fitness/ProgramCreator';
import ProgramImportWizard from './components/fitness/ProgramImportWizard';
import ExerciseCreator from './components/fitness/ExerciseCreator';
import CoachDashboard from './components/CoachDashboard';
import BusinessDashboard from './components/BusinessDashboard';
import BusinessEmployeeDashboard from './components/BusinessEmployeeDashboard';
import BusinessJoin from './components/BusinessJoin';
import BusinessRegistration from './components/BusinessRegistration';
import SupportPortal from './components/SupportPortal';
import CreateTicket from './components/CreateTicket';
import TicketDetail from './components/TicketDetail';
import AdminSupportDashboard from './components/AdminSupportDashboard';
import MealPlanningLanding from './components/MealPlanningLanding';
import FitnessLanding from './components/FitnessLanding';
import CoachingLanding from './components/CoachingLanding';
import BusinessLanding from './components/BusinessLanding';
import CommunityKitchenDashboard from './components/CommunityKitchen/CommunityKitchenDashboard';
import CommunityKitchenOnboarding from './components/CommunityKitchen/CommunityKitchenOnboarding';
import CommunityKitchenMatchDetail from './components/CommunityKitchen/CommunityKitchenMatchDetail';
import AIChatPanel from './components/AIChatPanel';
import ConsentBanner from './components/ConsentBanner';
import './App.css';

// Route tracking component 
function RouteTracker() {
  const location = useLocation();

  useEffect(() => {
    pageview(location.pathname + location.search);
  }, [location]);

  return null;
}

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    const mainElement = document.querySelector('main');
    if (mainElement) {
      mainElement.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }, [pathname]);

  return null;
}

function App() {
  const userString = localStorage.getItem('user');
  const user = userString ? JSON.parse(userString) : null;

  // Identify already-logged-in users on every app load. Login tokens are
  // long-lived, so most active users never pass through the login handlers
  // where identify used to happen — without this, nearly the whole
  // logged-in base shows up as anonymous in PostHog and per-user
  // retention/funnels are unmeasurable.
  useEffect(() => {
    if (user) {
      identifyUser(user.sub || user.email, { email: user.email });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Protected route component with improved debugging
  const ProtectedRoute = ({ children, requiredRole }) => {
    if (!user) {
      return <Navigate to="/" replace />;
    }

    if (requiredRole && !user[requiredRole]) {
      return <Navigate to="/" replace />;
    }

    return children;
  };

  return (
    <>
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#333',
            color: '#fff',
          },
        }}
      />
      <GoogleOAuthProvider 
        clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}
        onScriptLoadError={(error) => {
          console.error('Google Script Load Error:', error);
        }}
      >
        <Router>
          <RouteTracker />
          <ScrollToTop />
          <div className="app">
            <Navbar />
            <main>
              <div className="main-content">
                <Routes>
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/welcome" element={<LandingPage />} />
                  <Route path="/welcome/menu-preferences" element={
                    <ProtectedRoute>
                      <MenuPreferencesOnboarding />
                    </ProtectedRoute>
                  } />
                  <Route path="/meal-planning" element={<MealPlanningLanding />} />
                  <Route path="/fitness-training" element={<FitnessLanding />} />
                  <Route path="/coaching" element={<CoachingLanding />} />
                  <Route path="/for-business" element={<BusinessLanding />} />
                  <Route path="/menu" element={<MenuFilter />} />
                  <Route path="/menu/:id" element={<MenuFilter />} />
                  <Route 
                    path="/cart" 
                    element={<ShoppingCart />} 
                  />
                  <Route path="/faq" element={<FAQ />} />
                  <Route path="/dev/login" element={<DevLogin />} />
                  <Route 
                    path="/meal-plan/suggestion/:publicId" 
                    element={<MealPlanSuggestion />} 
                  />
                  <Route 
                    path="/meal-plan" 
                    element={user ? <MealPlan /> : <MealPlanPreview />} 
                  />
                  <Route 
                    path="/shared/:shareToken" 
                    element={<SharedMealPlan />} 
                  />
                  <Route 
                    path="/admin/upload" 
                    element={
                      <ProtectedRoute requiredRole="isAdmin">
                        <AdminMenuUpload />
                      </ProtectedRoute>
                    }
                  />
                  <Route 
                    path="/admin/dashboard" 
                    element={
                      <ProtectedRoute requiredRole="isAdmin">
                        <AdminDashboard />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/admin/fitness" 
                    element={
                      <ProtectedRoute requiredRole="isAdmin">
                        <FitnessAdminDashboard />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/affiliate/dashboard" 
                    element={
                      <ProtectedRoute requiredRole="isAffiliate">
                        <AffiliateDashboard />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/affiliate/register" 
                    element={user ? <AffiliateRegistration /> : <AffiliateRegistrationPreview />} 
                  />
                  <Route path="/creator/:slug" element={<CreatorPage />} />
                  <Route path="/creators" element={<CreatorsPage />} />
                  <Route path="/coaches" element={<CoachesPage />} />
                  <Route path="/coach/:slug" element={<CoachProfilePage />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/terms" element={<TermsOfService />} />
                  <Route path="/delete-account" element={<AccountDeletion />} />
                  <Route path="/pricing" element={<Pricing />} />
                  <Route path="/profile" element={
                    <ProtectedRoute>
                      <UserProfile />
                    </ProtectedRoute>
                  } />
                  <Route 
                    path="/subscription/success" 
                    element={
                      <ProtectedRoute>
                        <SubscriptionSuccess />
                      </ProtectedRoute>
                    }
                  />
                  <Route 
                    path="/my-menus" 
                    element={user ? (
                      <ProtectedRoute>
                        <UserDashboard />
                      </ProtectedRoute>
                    ) : <MyMenusPreview />} 
                  />
                  <Route path="/fitness" element={<FitnessHome />} />
                  <Route path="/fitness/programs" element={<FitnessHome />} />
                  <Route path="/fitness/program/:programId" element={<ProgramPage />} />
                  <Route path="/fitness/workouts" element={<WorkoutsListPage />} />
                  <Route path="/fitness/workout/:workoutId" element={<WorkoutPage />} />
                  <Route path="/fitness/exercises" element={<ExercisesListPage />} />
                  <Route path="/fitness/exercise/:exerciseId" element={<ExercisePage />} />
                  <Route path="/fitness/active/:workoutId" element={<ActiveWorkout />} />
                  <Route path="/fitness/history" element={<Navigate to="/fitness/my-workouts" replace />} />
                  <Route path="/fitness/session/:sessionId" element={<SessionDetailPage />} />
                  <Route 
                    path="/coach/dashboard" 
                    element={
                      <ProtectedRoute>
                        <CoachDashboard />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/coach/workout/create" 
                    element={
                      <ProtectedRoute>
                        <WorkoutCreator isCoach />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/coach/workout/:id/edit" 
                    element={
                      <ProtectedRoute>
                        <WorkoutCreator isCoach />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/coach/program/create" 
                    element={
                      <ProtectedRoute>
                        <ProgramCreator isCoach />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/coach/program/:id/edit" 
                    element={
                      <ProtectedRoute>
                        <ProgramCreator isCoach />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/coach/exercise/create" 
                    element={
                      <ProtectedRoute>
                        <ExerciseCreator isCoach />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/coach/exercise/:id/edit" 
                    element={
                      <ProtectedRoute>
                        <ExerciseCreator isCoach />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/fitness/workout/create" 
                    element={
                      <ProtectedRoute>
                        <WorkoutCreator />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/fitness/workout/:id/edit" 
                    element={
                      <ProtectedRoute>
                        <WorkoutCreator />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/fitness/program/create" 
                    element={
                      <ProtectedRoute>
                        <ProgramCreator />
                      </ProtectedRoute>
                    } 
                  />
                  <Route
                    path="/fitness/program/import"
                    element={
                      <ProtectedRoute>
                        <ProgramImportWizard />
                      </ProtectedRoute>
                    }
                  />
                  <Route 
                    path="/fitness/program/:id/edit" 
                    element={
                      <ProtectedRoute>
                        <ProgramCreator />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/fitness/exercise/create" 
                    element={
                      <ProtectedRoute>
                        <ExerciseCreator />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/fitness/exercise/:id/edit" 
                    element={
                      <ProtectedRoute>
                        <ExerciseCreator />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/fitness/my-workouts" 
                    element={
                      user ? (
                        <ProtectedRoute>
                          <MyWorkoutsPage />
                        </ProtectedRoute>
                      ) : (
                        <MyWorkoutsPreview />
                      )
                    } 
                  />
                  <Route 
                    path="/nutrition" 
                    element={<Navigate to="/nutrition/planner" replace />} 
                  />
                  <Route 
                    path="/nutrition/planner" 
                    element={<NutritionPlannerWizard />} 
                  />
                  <Route 
                    path="/nutrition/plans" 
                    element={<Navigate to="/meal-plan" replace />} 
                  />
                  <Route 
                    path="/nutrition/shared/:token" 
                    element={<SharedNutritionPlan />} 
                  />
                  <Route 
                    path="/coach/invite/:token" 
                    element={<CoachInvite />} 
                  />
                  <Route 
                    path="/support" 
                    element={
                      <ProtectedRoute>
                        <SupportPortal />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/support/new" 
                    element={
                      <ProtectedRoute>
                        <CreateTicket />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/support/ticket/:ticketId" 
                    element={
                      <ProtectedRoute>
                        <TicketDetail />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/admin/support" 
                    element={
                      <ProtectedRoute requiredRole="isAdmin">
                        <AdminSupportDashboard />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/business/register" 
                    element={
                      user ? <BusinessRegistration /> : <Navigate to="/" replace />
                    } 
                  />
                  <Route 
                    path="/business/dashboard" 
                    element={
                      <ProtectedRoute requiredRole="isBusinessOwner">
                        <BusinessDashboard />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/business/employee/dashboard" 
                    element={
                      <ProtectedRoute requiredRole="isBusinessEmployee">
                        <BusinessEmployeeDashboard />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/business/join/:joinToken" 
                    element={<BusinessJoin />} 
                  />
                  <Route 
                    path="/business/employee-invite/:token" 
                    element={<BusinessJoin mode="employee-invite" />} 
                  />
                  <Route 
                    path="/business/client-invite/:token" 
                    element={<BusinessJoin mode="client-invite" />} 
                  />
                  <Route 
                    path="/community-kitchen" 
                    element={
                      <ProtectedRoute>
                        <CommunityKitchenDashboard />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/community-kitchen/onboarding" 
                    element={
                      <ProtectedRoute>
                        <CommunityKitchenOnboarding />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/community-kitchen/match/:id" 
                    element={
                      <ProtectedRoute>
                        <CommunityKitchenMatchDetail />
                      </ProtectedRoute>
                    } 
                  />
                </Routes>
              </div>
            </main>
            <Footer />
            {user && <AIChatPanel />}
            <ConsentBanner />
          </div>
        </Router>
      </GoogleOAuthProvider>
    </>
  );
}

export default App; 